import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';
import { currentUserQueryKey, type CurrentUser } from '@/auth/useCurrentUser';

export type LegalStatus = components['schemas']['LegalStatusResponse'];
export type LegalAcceptance = components['schemas']['LegalAcceptanceResponse'];
export type LegalDocument = components['schemas']['LegalDocumentResponse'];
export type AcceptLegalBody = components['schemas']['AcceptLegalDocumentsRequest'];

export const legalAcceptanceQueryKey = ['legal', 'acceptance'] as const;

/**
 * A refusal the API explained in a sentence (a 400's first error): the box not
 * ticked, older wording, a document that changed since the page loaded. Shown
 * as it comes, the server's words.
 */
export class LegalAcceptanceRefusedError extends Error {
  constructor(
    message: string,
    readonly field: string,
  ) {
    super(message);
    this.name = 'LegalAcceptanceRefusedError';
  }
}

/**
 * The SaaS documents and the line to tick (M2-48), for the acceptance screen.
 * Read only while the screen is open. No retry: the screen shows its own
 * failure and a Try again.
 */
export function useLegalAcceptance(enabled: boolean) {
  return useQuery({
    queryKey: legalAcceptanceQueryKey,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/legal/acceptance');
      if (!response.ok || !data)
        throw new Error(`GET /api/v1/legal/acceptance failed: ${response.status}`);
      return data;
    },
    enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** `/me`'s `legal` as the acceptance answer implies it — the same summary `LegalAcceptances.Summary` makes. */
export function statusFrom(acceptance: LegalAcceptance): LegalStatus {
  return {
    state: acceptance.state,
    dueOn: acceptance.dueOn,
    outstanding: acceptance.documents.filter((d) => d.state !== 'Accepted').map((d) => d.kind),
  };
}

/**
 * Accept every document as shown (M2-48). On success the answer goes into
 * `/me`'s `legal` at once, so the gate lifts without waiting for `/me`'s
 * five-minute staleTime, and `/me` is invalidated too, so the server's own
 * word follows.
 *
 * ⚠️ A 400 re-reads the documents: "has changed since this page loaded" means
 * the screen is showing an old version, and the person must see the new one
 * before ticking again.
 */
export function useAcceptLegal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: AcceptLegalBody) => {
      const { data, error, response } = await apiClient.POST('/api/v1/legal/acceptance', { body });
      if (!response.ok || !data) {
        const errors = (error as { errors?: Record<string, string[]> } | undefined)?.errors;
        const first = errors ? Object.entries(errors)[0] : undefined;
        if (response.status === 400 && first?.[1]?.[0]) {
          throw new LegalAcceptanceRefusedError(first[1][0], first[0]);
        }
        throw new Error(`POST /api/v1/legal/acceptance failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: (acceptance) => {
      queryClient.setQueryData(legalAcceptanceQueryKey, acceptance);
      queryClient.setQueryData<CurrentUser>(currentUserQueryKey, (me) =>
        me ? { ...me, legal: statusFrom(acceptance) } : me,
      );
      void queryClient.invalidateQueries({ queryKey: currentUserQueryKey });
    },
    onError: (error) => {
      if (error instanceof LegalAcceptanceRefusedError) {
        void queryClient.invalidateQueries({ queryKey: legalAcceptanceQueryKey });
      }
    },
  });
}
